import { useMutation, useQueryClient } from "@tanstack/react-query";
import { contextsKeys } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { myInvitationsKeys } from "Auth/Api/Queries/Invitations/useGetMyInvitationsQuery";
import {
  type AcceptInvitationResult,
  postInvitationsPublicIDAccept,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  onSuccess?: (result: AcceptInvitationResult) => void;
  onError?: (error: unknown) => void;
};

/**
 * Accepts a pending RoleInvitation. Returns the activated IdentityUser's PublicID so the caller
 * can immediately switch context to it via `useSwitchContextMutation`.
 */
export const useAcceptInvitationMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails((publicID: string) =>
      postInvitationsPublicIDAccept(publicID),
    ),
    onSuccess: (result: AcceptInvitationResult) => {
      // The newly-Active IdentityUser appears in the context list; the old invitation row
      // disappears from the inbox. Invalidate both so any visible UI refreshes.
      queryClient.invalidateQueries({ queryKey: myInvitationsKeys.all });
      queryClient.invalidateQueries({ queryKey: contextsKeys.all });
      onSuccess?.(result);
    },
    onError,
  });
};
